import { useEffect, useMemo, useRef, useState } from "react";
import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ApiError,
  changeMePassword,
  confirmEmailVerification,
  logout as apiLogout,
  patchMeProfile,
  requestMeEmailVerification,
  setAuthToken,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { unscopedKey, removeAuthenticatedFromCache } from "@/lib/queryClient";
import { revokeAllThumbnails } from "@/features/coach-review/hooks/useThumbnail";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type { UserProfile } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";

/**
 * M9a B1–B8 — `/me` account self-service shell.
 *
 * Four tabs (Profile · Password · Email · Sessions). `?tab=` route
 * search-param drives the active tab; `.catch(undefined)` falls
 * back to `profile`.
 *
 * Auth gate (B7): unauthenticated users redirect to
 * `/login?next=/me` (preserves their landing intent). The redirect
 * fires from a `useEffect` so the SPA shell still mounts on a
 * direct nav.
 */

type AccountTab = "profile" | "password" | "email" | "sessions";
const VALID_TABS: AccountTab[] = ["profile", "password", "email", "sessions"];

const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Athens",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const COMMON_LOCALES = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "es-ES", label: "Español" },
  { value: "fr-FR", label: "Français" },
  { value: "de-DE", label: "Deutsch" },
  { value: "pt-BR", label: "Português (BR)" },
  { value: "ja-JP", label: "日本語" },
];

export function AccountPage() {
  const me = useMe();
  const navigate = useNavigate({ from: "/me" });
  const search = useSearch({ from: "/me" });
  const tab: AccountTab = VALID_TABS.includes(search.tab as AccountTab)
    ? (search.tab as AccountTab)
    : "profile";

  // B7 — auth gate: unauth → /login?next=/me. Ref-flag prevents
  // the effect re-firing once the URL changes (TanStack Router
  // keeps the parent briefly mounted on intra-effect navigate).
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (me.isLoading) return;
    if (me.data?.user) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: "/me" },
      replace: true,
    });
  }, [me.isLoading, me.data, navigate]);

  if (me.isLoading) {
    return (
      <main
        data-testid="me-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }
  if (!me.data?.user) {
    return null;
  }

  return (
    <main
      data-testid="me-page"
      className="min-h-screen bg-canvas"
    >
      <header className="px-4 py-3 border-b border-rule">
        <h1 className="text-sm font-display uppercase tracking-wider text-ink">
          Account · @{me.data.user.username}
        </h1>
      </header>

      <nav
        data-testid="me-tabs"
        role="tablist"
        className="px-4 inline-flex gap-1 border-b border-rule"
      >
        {VALID_TABS.map((t) => (
          <TabButton
            key={t}
            active={tab === t}
            onClick={() =>
              navigate({
                search: (prev) => ({ ...prev, tab: t }),
                replace: false,
              })
            }
            testid={`me-tab-${t}`}
          >
            {labelForTab(t)}
          </TabButton>
        ))}
      </nav>

      <section
        data-testid="me-content"
        data-active-tab={tab}
        className="p-4 max-w-2xl mx-auto"
      >
        {tab === "profile" ? <ProfileTab profile={me.data.profile} /> : null}
        {tab === "password" ? <PasswordTab /> : null}
        {tab === "email" ? <EmailTab profile={me.data.profile} /> : null}
        {tab === "sessions" ? <SessionsTab /> : null}
      </section>
    </main>
  );
}

function labelForTab(tab: AccountTab): string {
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}

function TabButton({
  active,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      className="px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink data-[active=true]:text-ink data-[active=true]:border-b-2 data-[active=true]:border-accent transition-colors"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// B2 + B3 — Profile tab
// ---------------------------------------------------------------------------

function ProfileTab({ profile }: { profile: UserProfile }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const browserTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const seedFromProfile = (p: UserProfile) => ({
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    timezone: p.timezone ?? browserTz,
    locale: p.locale ?? "en-US",
    preferred_contact_method:
      (p.preferred_contact_method as "email" | "phone" | "none") ?? "none",
  });

  const [draft, setDraft] = useState(() => seedFromProfile(profile));
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether the form has unsaved edits. We only re-seed from
  // an upstream profile refetch when the draft is pristine, so a
  // background `/api/me` refresh (window-focus refetch, scope swap,
  // another mutation invalidating the cache) cannot wipe what the
  // user is typing.
  const [dirty, setDirty] = useState(false);
  // Snapshot the baseline used for dirty-comparison, refreshed each
  // time we reseed from the server.
  const baselineRef = useRef(seedFromProfile(profile));

  // Refetch reseed: only fire when the form has no unsaved edits.
  useEffect(() => {
    if (dirty) return;
    const seeded = seedFromProfile(profile);
    baselineRef.current = seeded;
    setDraft(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const updateField = <K extends keyof typeof draft>(
    key: K,
    value: (typeof draft)[K],
  ) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      // Recompute dirty against the last server snapshot, NOT the
      // initial mount value, so a save → reseed → no-op edit cycle
      // returns to pristine correctly.
      const base = baselineRef.current;
      const isDirty =
        next.first_name !== base.first_name ||
        next.last_name !== base.last_name ||
        next.email !== base.email ||
        next.phone !== base.phone ||
        next.timezone !== base.timezone ||
        next.locale !== base.locale ||
        next.preferred_contact_method !== base.preferred_contact_method;
      setDirty(isDirty);
      return next;
    });
  };

  // `null` clears the stored field on the backend; `undefined` would
  // be dropped by `model_dump(exclude_unset=True)` and leave the
  // server value untouched. Always send the typed value (trimmed),
  // mapping empty → null so blanking out a field actually persists.
  const toBody = () => {
    const norm = (s: string): string | null => {
      const t = s.trim();
      return t.length === 0 ? null : t;
    };
    return {
      first_name: norm(draft.first_name),
      last_name: norm(draft.last_name),
      email: norm(draft.email),
      phone: norm(draft.phone),
      timezone: norm(draft.timezone),
      locale: norm(draft.locale),
      preferred_contact_method: draft.preferred_contact_method,
    };
  };

  const mut = useMutation({
    mutationFn: () => patchMeProfile(toBody()),
    onSuccess: () => {
      setSuccess(true);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      toast.success("Profile updated");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That email is already in use.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't update profile", { description: formatErr(err) });
    },
  });

  return (
    <form
      data-testid="me-profile-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSuccess(false);
        setError(null);
        mut.mutate();
      }}
      className="flex flex-col gap-3"
    >
      <h2 className="text-base font-display uppercase tracking-wider text-ink mb-1">
        Profile
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <TextField
          label="First name"
          value={draft.first_name}
          onChange={(v) => updateField("first_name", v)}
          testid="me-profile-first-name"
          maxLength={120}
        />
        <TextField
          label="Last name"
          value={draft.last_name}
          onChange={(v) => updateField("last_name", v)}
          testid="me-profile-last-name"
          maxLength={120}
        />
      </div>

      <TextField
        label="Email"
        type="email"
        value={draft.email}
        onChange={(v) => updateField("email", v)}
        testid="me-profile-email"
        autoComplete="email"
        maxLength={320}
      />
      <TextField
        label="Phone"
        type="tel"
        value={draft.phone}
        onChange={(v) => updateField("phone", v)}
        testid="me-profile-phone"
        autoComplete="tel"
        maxLength={64}
      />

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Timezone
        </span>
        {/* Make the browser-detected tz the first option when it's
         * outside the curated COMMON_TIMEZONES list. */}
        <TkticSelect
          value={draft.timezone}
          onChange={(next) => updateField("timezone", next)}
          data-testid="me-profile-timezone"
          ariaLabel="Timezone"
          options={[
            ...(!COMMON_TIMEZONES.includes(draft.timezone) && draft.timezone
              ? [
                  {
                    value: draft.timezone,
                    label: `${draft.timezone} (detected)`,
                  },
                ]
              : []),
            ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: tz })),
          ]}
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Language
        </span>
        <TkticSelect
          value={draft.locale}
          onChange={(next) => updateField("locale", next)}
          data-testid="me-profile-locale"
          ariaLabel="Language"
          options={COMMON_LOCALES.map((l) => ({
            value: l.value,
            label: l.label,
          }))}
        />
      </label>

      <div
        data-testid="me-profile-contact"
        className="flex flex-col gap-1 text-xs"
      >
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Preferred contact method
        </span>
        <div className="inline-flex gap-1">
          {(["email", "phone", "none"] as const).map((method) => (
            <button
              key={method}
              type="button"
              onClick={() => updateField("preferred_contact_method", method)}
              data-testid={`me-profile-contact-${method}`}
              data-active={draft.preferred_contact_method === method ? "true" : undefined}
              className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
            >
              {method}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p
          data-testid="me-profile-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="me-profile-success"
          className="text-good text-xs font-mono"
        >
          Profile saved.
        </p>
      ) : null}

      <Button
        type="submit"
        size="sm"
        disabled={mut.isPending}
        data-testid="me-profile-save"
        className="self-start"
      >
        {mut.isPending ? "Saving…" : "Save profile"}
      </Button>
    </form>
  );
}

function TextField({
  label,
  value,
  onChange,
  testid,
  type = "text",
  autoComplete,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
  type?: string;
  autoComplete?: string;
  maxLength?: number;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        data-testid={testid}
        className="rounded border border-rule bg-surface px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// B4 — Password tab
// ---------------------------------------------------------------------------

function PasswordTab() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      changeMePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Current password is incorrect.",
        );
      } else if (err instanceof ApiError && err.status === 403) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Password changes are only available for database users.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't change password", { description: formatErr(err) });
    },
  });

  return (
    <form
      data-testid="me-password-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSuccess(false);
        setError(null);
        if (next.length < 8) {
          setError("New password must be at least 8 characters.");
          return;
        }
        if (next !== confirm) {
          setError("New password and confirmation don't match.");
          return;
        }
        mut.mutate();
      }}
      className="flex flex-col gap-3"
    >
      <h2 className="text-base font-display uppercase tracking-wider text-ink mb-1">
        Password
      </h2>
      <TextField
        label="Current password"
        type="password"
        value={current}
        onChange={setCurrent}
        testid="me-password-current"
        autoComplete="current-password"
      />
      <TextField
        label="New password"
        type="password"
        value={next}
        onChange={setNext}
        testid="me-password-new"
        autoComplete="new-password"
      />
      <TextField
        label="Confirm new password"
        type="password"
        value={confirm}
        onChange={setConfirm}
        testid="me-password-confirm"
        autoComplete="new-password"
      />

      <p className="text-[0.6rem] font-mono text-ink-muted">
        Changing your password signs out every other session you have open.
      </p>

      {error ? (
        <p
          data-testid="me-password-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="me-password-success"
          className="text-good text-xs font-mono"
        >
          Password updated. Other sessions have been signed out.
        </p>
      ) : null}

      <Button
        type="submit"
        size="sm"
        disabled={mut.isPending}
        data-testid="me-password-save"
        className="self-start"
      >
        {mut.isPending ? "Saving…" : "Update password"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// B5 — Email tab
// ---------------------------------------------------------------------------

function EmailTab({ profile }: { profile: UserProfile }) {
  const toast = useToast();
  const verified = !!profile.email_verified_at;
  const hasEmail = !!profile.email;
  const [devToken, setDevToken] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const requestMut = useMutation({
    mutationFn: requestMeEmailVerification,
    onSuccess: (resp) => {
      setRequestSuccess(true);
      // Privacy invariant: dev-token surfaces ONLY when the backend
      // returns it (TKTIC_DEV_TOKEN_DELIVERY=1). Never logged.
      if (resp.verification_token) {
        setDevToken(resp.verification_token);
      }
      toast.success("Verification email sent");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Add an email on the Profile tab before requesting verification.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't send verification email", {
        description: formatErr(err),
      });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (t: string) => confirmEmailVerification({ token: t }),
    onSuccess: () => {
      setConfirmSuccess(true);
      setDevToken(null);
      setToken("");
      void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      toast.success("Email verified");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That verification token is invalid or expired.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't verify email", { description: formatErr(err) });
    },
  });

  return (
    <section
      data-testid="me-email-section"
      className="flex flex-col gap-3"
    >
      <h2 className="text-base font-display uppercase tracking-wider text-ink mb-1">
        Email
      </h2>

      <div className="flex items-center gap-2 text-sm">
        <span className="text-ink">
          {profile.email || "(none on file — set one on the Profile tab)"}
        </span>
        {hasEmail ? (
          <span
            data-testid="me-email-verified-pill"
            data-verified={verified ? "true" : "false"}
            className={`rounded border bg-surface/70 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${
              verified
                ? "border-good/40 text-good"
                : "border-destructive/40 text-destructive"
            }`}
          >
            {verified ? "Verified" : "Unverified"}
          </span>
        ) : null}
      </div>
      {verified ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">
          Verified on {profile.email_verified_at}
        </p>
      ) : hasEmail ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">
          Email is on file but not verified. Send a verification email to
          confirm ownership.
        </p>
      ) : null}

      <div className="flex flex-col gap-2 rounded border border-rule bg-elevated p-3">
        <h3 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Request verification
        </h3>
        <button
          type="button"
          disabled={!hasEmail || verified || requestMut.isPending}
          onClick={() => {
            setRequestSuccess(false);
            setError(null);
            requestMut.mutate();
          }}
          data-testid="me-email-request"
          className="self-start rounded border border-accent bg-accent px-2 py-1 text-xs font-display uppercase tracking-wider text-accent-ink transition-colors hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {requestMut.isPending ? "Sending…" : "Send verification email"}
        </button>
        {requestSuccess ? (
          <p
            data-testid="me-email-request-success"
            className="text-[0.65rem] font-mono text-good"
          >
            Verification email sent. Paste the token below to confirm.
          </p>
        ) : null}
        {devToken ? (
          <div
            data-testid="me-email-dev-token"
            className="flex flex-col gap-1 rounded border border-rule bg-surface/70 p-2 text-[0.6rem] font-mono"
          >
            <span className="text-ink-muted">Dev verification token:</span>
            <code className="text-ink break-all">{devToken}</code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(devToken);
                setToken(devToken);
              }}
              data-testid="me-email-dev-copy"
              className="self-start rounded border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Copy + paste into the confirm field
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 rounded border border-rule bg-elevated p-3">
        <h3 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Confirm
        </h3>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Verification token
          </span>
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="me-email-token"
            className="rounded border border-rule bg-surface px-2 py-1.5 text-xs font-mono text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <Button
          type="button"
          size="sm"
          disabled={token.length < 16 || confirmMut.isPending}
          onClick={() => {
            setConfirmSuccess(false);
            setError(null);
            confirmMut.mutate(token.trim());
          }}
          data-testid="me-email-confirm"
          className="self-start"
        >
          {confirmMut.isPending ? "Confirming…" : "Confirm email"}
        </Button>
        {confirmSuccess ? (
          <p
            data-testid="me-email-confirm-success"
            className="text-[0.65rem] font-mono text-good"
          >
            Email verified. Thank you for confirming.
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          data-testid="me-email-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// B6 — Sessions tab (minimal per locked scope; full list 🔵)
// ---------------------------------------------------------------------------

function SessionsTab() {
  const navigate = useNavigate({ from: "/me" });
  const applyAnon = useAuthStore((s) => s.applyAnonymous);
  const clearScope = useScopeStore((s) => s.clear);
  const [signOutPending, setSignOutPending] = useState(false);

  const onSignOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      try {
        await apiLogout();
      } catch {
        /* server may already have killed the session; clear locally */
      }
    } finally {
      setAuthToken(null);
      applyAnon();
      clearScope();
      removeAuthenticatedFromCache();
      revokeAllThumbnails();
      void navigate({ to: "/login", replace: true });
    }
  };

  return (
    <section
      data-testid="me-sessions-section"
      className="flex flex-col gap-3"
    >
      <h2 className="text-base font-display uppercase tracking-wider text-ink mb-1">
        Sessions
      </h2>
      <p className="text-ink text-sm">
        Sign out of every other browser by changing your password on the
        Password tab — the server revokes all other sessions on that change.
      </p>
      <p className="text-[0.6rem] font-mono text-ink-muted">
        A per-session list (last-used / device / IP) is a planned
        post-cutover follow-up; the backend doesn't expose it yet.
      </p>
      <button
        type="button"
        onClick={() => void onSignOut()}
        disabled={signOutPending}
        data-testid="me-sessions-signout"
        className="self-start rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-3 py-1.5 text-sm font-display uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {signOutPending ? "Signing out…" : "Sign out of this session"}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string"
      ? err.detail
      : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}
